if (!customElements.get("sticky-product-summary")) {
  customElements.define(
    "sticky-product-summary",
    class StickyProductSummary extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.raf = null;
      }

      connectedCallback() {
        this.mainProduct = document.querySelector("product-info[id^='MainProduct']");
        // Source price block inside the main product info (the one users see first).
        this.sourcePrice = document.getElementById(`Price-${this.sectionId}`);
        // Price container inside this sticky bar (mirror target).
        this.summaryPrice = document.getElementById(`StickySummaryPrice-${this.sectionId}`);
        this.previewEl = this.querySelector("[data-preview]");
        this.summaryImg = this.previewEl ? this.previewEl.querySelector("img") : null;

        // Nothing to observe against -> keep the bar hidden.
        if (!this.sourcePrice) return;

        this.mql = window.matchMedia(
          (window.MinimogTheme && MinimogTheme.config && MinimogTheme.config.mediaQueryMobile) ||
            "(max-width: 767px)"
        );

        this.onScroll = this.updatePosition.bind(this);
        this.onResize = this.updatePosition.bind(this);
        this.onMediaChange = this.handleMediaChange.bind(this);

        if (typeof this.mql.addEventListener === "function") {
          this.mql.addEventListener("change", this.onMediaChange);
        } else if (typeof this.mql.addListener === "function") {
          this.mql.addListener(this.onMediaChange);
        }

        this.handleMediaChange();

        // Keep the sticky price in sync with the main price (variant changes, etc.).
        this.syncPrice();
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.sourcePrice, { childList: true, subtree: true, characterData: true });

        // Keep the thumbnail (and its preview) in sync with the selected variant.
        this.onVariantChange = (e) => {
          if (!e || !e.data) return;
          const isMainProduct =
            !this.mainProduct || e.data.sectionId === this.mainProduct.dataset.sectionId;
          if (!isMainProduct) return;
          this.updateImage(e.data.variant);
        };
        if (window.MinimogEvents && MinimogTheme.pubSubEvents) {
          this.variantUnsubscriber = MinimogEvents.subscribe(
            MinimogTheme.pubSubEvents.variantChange,
            this.onVariantChange
          );
        }

        // Tapping the thumbnail opens a full-screen preview of the current image.
        if (this.previewEl) {
          this.onPreviewOpen = () => this.openPreview();
          this.previewEl.addEventListener("click", this.onPreviewOpen);
          this.previewEl.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              this.openPreview();
            }
          });
        }
      }

      updateImage(variant) {
        if (!variant || !variant.featured_image || !this.summaryImg) return;
        const src = variant.featured_image.src;
        if (!src) return;

        const sized =
          typeof window.getSizedImageUrl === "function"
            ? window.getSizedImageUrl(src, "200x")
            : src;
        this.summaryImg.src = sized;
        if (variant.featured_image.alt) this.summaryImg.alt = variant.featured_image.alt;
        this.summaryImg.removeAttribute("srcset");

        // Update the full-resolution source used by the preview overlay.
        if (this.previewEl) {
          const full =
            typeof window.getSizedImageUrl === "function"
              ? window.getSizedImageUrl(src, "1200x")
              : src;
          this.previewEl.dataset.fullSrc = full;
          if (this.previewImg) this.previewImg.src = full;
        }
      }

      openPreview() {
        if (!this.previewEl) return;
        const fullSrc = this.previewEl.dataset.fullSrc || (this.summaryImg && this.summaryImg.src);
        if (!fullSrc) return;

        if (!this.previewOverlay) this.buildPreviewOverlay();
        this.previewImg.src = fullSrc;
        this.previewOverlay.classList.add("is-open");
        document.body.classList.add("m-sticky-summary-preview-open");
      }

      closePreview() {
        if (!this.previewOverlay) return;
        this.previewOverlay.classList.remove("is-open");
        document.body.classList.remove("m-sticky-summary-preview-open");
      }

      buildPreviewOverlay() {
        const overlay = document.createElement("div");
        overlay.className = "m-sticky-summary__preview";
        overlay.innerHTML =
          '<button type="button" class="m-sticky-summary__preview-close" aria-label="Close">&times;</button>' +
          '<img class="m-sticky-summary__preview-img" alt="">';
        document.body.appendChild(overlay);

        this.previewOverlay = overlay;
        this.previewImg = overlay.querySelector(".m-sticky-summary__preview-img");

        overlay.addEventListener("click", (e) => {
          if (e.target === overlay || e.target.closest(".m-sticky-summary__preview-close")) {
            this.closePreview();
          }
        });
        this.onPreviewKeydown = (e) => {
          if (e.key === "Escape") this.closePreview();
        };
        document.addEventListener("keydown", this.onPreviewKeydown);
      }

      disconnectedCallback() {
        this.teardownObserver();
        if (this.priceObserver) this.priceObserver.disconnect();
        window.removeEventListener("scroll", this.onScroll);
        window.removeEventListener("resize", this.onResize);
        if (this.mql) {
          if (typeof this.mql.removeEventListener === "function") {
            this.mql.removeEventListener("change", this.onMediaChange);
          } else if (typeof this.mql.removeListener === "function") {
            this.mql.removeListener(this.onMediaChange);
          }
        }
      }

      handleMediaChange() {
        if (this.mql.matches) {
          this.setupObserver();
        } else {
          this.teardownObserver();
          this.hide();
        }
      }

      setupObserver() {
        if (this.observer) return;
        const headerHeight = (window.MinimogSettings && MinimogSettings.headerHeight) || 66;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // The observer root's top edge is pushed down by headerHeight via
              // rootMargin, so the source price stops intersecting the moment its
              // top crosses that line (top ~= headerHeight), NOT when top < 0.
              // Compare against headerHeight so we reliably catch the crossing and
              // don't mistake "scrolled above" for "still below the viewport".
              const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < headerHeight;
              scrolledPast ? this.show() : this.hide();
            });
          },
          { threshold: 0, rootMargin: `-${headerHeight}px 0px 0px 0px` }
        );
        this.observer.observe(this.sourcePrice);
      }

      teardownObserver() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }

      show() {
        if (this.classList.contains("is-visible")) return;
        this.classList.add("is-visible");
        document.documentElement.classList.add("sticky-summary-show");
        window.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("resize", this.onResize);
        this.updatePosition();
      }

      hide() {
        if (!this.classList.contains("is-visible")) return;
        this.classList.remove("is-visible");
        document.documentElement.classList.remove("sticky-summary-show");
        window.removeEventListener("scroll", this.onScroll);
        window.removeEventListener("resize", this.onResize);
      }

      updatePosition() {
        if (this.raf) return;
        this.raf = window.requestAnimationFrame(() => {
          this.raf = null;
          // Position the bar right below the visible bottom edge of the header.
          // When the header is hidden (scroll-down), the wrapper is translated
          // off-screen, so its bottom clamps to 0 and the bar sits at the top.
          let offset = 0;
          const headerWrapper = document.querySelector(".m-header__wrapper");
          if (headerWrapper) {
            const rect = headerWrapper.getBoundingClientRect();
            offset = Math.max(0, rect.bottom);
          }
          this.style.setProperty("--m-summary-offset-top", `${offset}px`);
        });
      }

      syncPrice() {
        if (!this.summaryPrice || !this.sourcePrice) return;
        const source = this.sourcePrice.querySelector(".m-price");
        if (source) {
          this.summaryPrice.innerHTML = source.outerHTML;
        }
      }
    }
  );
}
